/**
 * ProgrammingDetails component is used to display and manage various aspects of a programming activity within a course.
 * It includes functionalities such as managing submissions, testing submissions, displaying problem statements, and showing autograding configurations.
 * Split component is used to divide the screen into two parts, with the left part displaying the submission management 
 * and testing components, and the right part displaying the problem statement, test cases, and autograding configuration components.
 * The component uses several child components:
 * - ManageSubmission: For managing the submission of code
 * - TestSubmission: For testing the submitted code
 * - SubjectiveGrades: For managing subjective grading
 * - ProblemStatement: For displaying the problem statement of the activity
 * - DisplayTestCases: For displaying test cases related to the activity
 * - AutogradingConfiguration: For displaying and configuring autograding settings
 *
 * The component also handles reading additional files provided by the instructor and displays them if available.
 */

import React, { useState, useEffect, useRef } from "react";
import ProblemStatement from "../ProblemStatement/ProblemStatement.jsx";
import ManageSubmission from "../ManageSubmissions/ManageSubmissions.jsx";
import TestSubmission from "../TestSubmission/TestSubmission.jsx";
import DisplayTestCases from "../DisplayTestCases/DisplayTestCases.jsx";
import AutogradingConfiguration from "../AutogradingConfiguration/AutogradingConfiguration.jsx";
import StudentPreview from "../StudentPreview/StudentPreview.jsx"
import Split from '@uiw/react-split';
import Alert from "react-bootstrap/Alert";

export default function ProgrammingDetails({
  programId,
  changeAccess,
  details,
  access,
  activity_title,
  activity_description,
  provideAdditionalFiles,
  instructorAdditionalFiles,
  document,
}) {

  // ---useRef variables to invoke clicking action programmatically of buttons inside testcase results accordion when corresponding buttons in editor footer are clicked ---//
  const practiceMarks = useRef(null)
  const evaluateMarks = useRef(null)

  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [testResults, setTestResults] = useState([]);
  const [show, setShow] = useState(true);

  //boolean variable to display alert message about markers usage for template coding
  const [showInfo, setShowInfo] = useState(true);

  //boolean variable to display student preview for template coding 
  const [showStudentPreview, setShowStudentPreview] = useState(false);

  //usestate varialbe for storing student preview code object 
  const [studentPreviewCode, setStudentPreviewCode] = useState(null);

  //string variable to store, add style and display the student preview code  inside student preview accordion
  const [code, setCode] = useState("");

  //boolean variable for template coding activity
  const [isTemplateCode, setIsTemplateCode] = useState(false);

  //State to store the content of the additional files provided by the instructor
  const [
    instructorAdditionalFilesContent,
    setInstructorAdditionalFilesContent,
  ] = useState({});

  //side effect to convert the recieved student previeww code to string
  useEffect(()=>{
      const fetchCode = async () => {
        await objectToString(studentPreviewCode);
      };
      fetchCode();
    },[studentPreviewCode])

  //This useEffect is used to read the content of the additional files provided by the instructor
  useEffect(() => {
    const readFiles = async () => {
      const newFileContents = {};
      //Iterating over the languages for which the instructor has provided additional files
      for (const [language, files] of Object.entries(
        instructorAdditionalFiles
      )) {
        newFileContents[language] = {};

        //Iterating over the files provided by the instructor for the current language
        for (const file of files) {
          //Reading the content of the file using the readFile function
          const content = await readFile(file);
          newFileContents[language][file.name] = content;
        }
      }
      //Setting the content of the additional files in the state
      setInstructorAdditionalFilesContent(newFileContents);
    };

    readFiles();
  }, [instructorAdditionalFiles]);

  //Function to read the content of a file
  const readFile = (file) => {
    return new Promise((resolve, reject) => {
      //Creating a new FileReader object
      const reader = new FileReader();
      //Setting the onload and onerror event handlers
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      //Reading the content of the file as text
      reader.readAsText(file);
    });
  };

  const renderAdditionalFilesAlert = () => {
    //Filtering the languages for which the instructor has provided additional files
    const languagesWithFiles = Object.keys(instructorAdditionalFiles).filter(
      (language) => instructorAdditionalFiles[language].length > 0
    );

    if (languagesWithFiles.length === 0) {
      return null;
    }
    //Rendering an alert to inform the student about the additional files provided by the instructor
    return (
      <Alert variant="info">
        The instructor has provided <b>additional files</b> for this activity.
        These files can be downloaded by clicking on them. Use them for Option
        1, but remember to submit only the file you have written. Additional
        files for:
        <ul>
          {languagesWithFiles.map((language) => (
            <li key={language}>
              {language}
              {" : "}
              {instructorAdditionalFiles[language].map((file, index) => (
                <a
                  key={index}
                  href={URL.createObjectURL(file)}
                  download={file.name}
                  style={{ marginRight: "8px" }}
                >
                  <u>{file.name}</u>
                  {""}
                </a>
              ))}
            </li>
          ))}
        </ul>
        The integrated editor will also display the additional files, but they
        will be read-only.
      </Alert>
    );
  };

  //function to handle opening reference file document in new tab on clicking reference file link 
  const openReferenceDocument = () => {
    const pdfUrl = "/Template_Coding_Reference_Document-1.pdf";
    const newTab = window.open(pdfUrl, "_blank");
  
    if (newTab) {
      newTab.document.title = "Template Coding Reference Document";
    }
  };

  //helper function to convert student preview code object to string format
  const objectToString = (studentPreviewCode) => {
    if (studentPreviewCode) {
      let string = Object.keys(studentPreviewCode)
        .map((file) => `File: ${file}\n${studentPreviewCode[file]}`)
        .join("\n\n"); 
        setCode(string); 
        return string;
    }
  }
  
  return (
    <>
      <div>
        {show && (
          <>
            <Alert variant="primary" onClose={() => setShow(false)} dismissible>
              Please either upload your code file using the <b>'Choose File'</b>{" "}
              button and using the <b>'Upload'</b> button or write your code in
              the editor below and click <b>'Submit Code'</b>. You only need to do
              one of these actions to submit your solution.
            </Alert>
          </>
        )}
      </div>
      <div>
        {/* Alert info message instructing about how to use start and end markers to design template coding activity */}
        {showInfo && (
          <>
            <Alert variant="primary" onClose={() => setShowInfo(false)} dismissible>
              To create editable sections please add start (<b>/* --- Start of student code, Don't Remove or edit these markers --- */</b>) and end (<b>/* --- End of student code, Don't Remove or edit these markers --- */</b>) markers in the driver code before submitting. Check this <span style={{ color: "blue", cursor: "pointer", textDecoration: "underline" }} onClick={openReferenceDocument}>
              reference file </span> to know more about how to add markers.
            </Alert>
          </>
        )}
      </div>
      <div>{provideAdditionalFiles && renderAdditionalFilesAlert()}</div>
      <Split>
        <div style={{ width: "70%", minWidth: "40%" }}>
          <div>
            {access?.enable_manage_submission ? (
              <ManageSubmission
                programId={programId}
                changeAccess={changeAccess}
                access={access}
                submission={details}
                setSelectedSubmission={setSelectedSubmission}
                setTestResults={setTestResults}
                instructorAdditionalFilesContent={
                  instructorAdditionalFilesContent
                }
                setShowStudentPreview={setShowStudentPreview}
                studentPreviewCodeode={studentPreviewCode}
                setStudentPreviewCode={setStudentPreviewCode}
                isTemplateCode={isTemplateCode}
                setIsTemplateCode={setIsTemplateCode}
                practiceMarks={practiceMarks}
                evaluateMarks={evaluateMarks}
              />
            ) : null}
          </div>
          <div>
            <TestSubmission
              programId={programId}
              selectedSubmission={selectedSubmission}
              changeAccess={changeAccess}
              access={access}
              setTestResults={setTestResults}
              testResults={testResults}
              practiceMarks={practiceMarks}
              evaluateMarks={evaluateMarks}
            />
          </div>
          {/* <div>
            <SubjectiveGrades
              programId={programId}
              selectedSubmission={selectedSubmission}
            />
          </div> */}
        </div>
        <div
          style={{
            minWidth: "20%",
            flex: 1,
            maxHeight: "100%",
            overflow: "auto",
          }}
        >
          {access?.enable_display_problem_statement ? (
            <ProblemStatement
              activity_description={activity_description}
              activity_title={activity_title}
              document={document}
            />
          ) : null}

          <DisplayTestCases programId={programId} testcasesData={details} />

          <AutogradingConfiguration
            isCaseSensitive={details?.case_sensitive}
            isIgnoreWhiteSpace={details?.ignore_whitespaces}
            isIndentationPercentage={details?.indentation_percentage}
            iscompareOnlyNumber={details?.compare_only_numbers}
            allow_delimeters={details?.allow_delimiters}
            errorRate={details?.error_rate}
            isOrderImportant={details?.order_is_important}
            linkerFlags={details?.linker_flags}
          />
          {/* student preview code accordion */}
          {isTemplateCode && showStudentPreview &&(
            <>
              <StudentPreview code={code}/>
            </>
          )}
        </div>
      </Split>
    </>
  );
}
